import axiosInstance from '../../../core/api/interceptors/axiosInterceptor';

/**
 * Create a single inventory item
 */
export const createInventoryItem = async (itemData) => {
  const response = await axiosInstance.post('/InventoryItem', itemData);
  return response.data;
};

/**
 * Bulk create inventory items (used during purchase invoice receiving)
 */
export const bulkCreateInventoryItems = async (bulkData) => {
  const response = await axiosInstance.post('/InventoryItem/bulk', bulkData);
  return response.data;
};

/**
 * Get available inventory items for a variant (for sales selection)
 * @param {number} variantId
 * @param {number|null} storeId
 */
export const getAvailableItems = async (variantId, storeId = null) => {
  const params = storeId ? { storeId } : {};
  const response = await axiosInstance.get(`/InventoryItem/variant/${variantId}/available`, { params });
  return response.data;
};

/**
 * Search inventory item by IMEI
 * @param {string} imei
 */
export const getItemByIMEI = async (imei) => {
  const response = await axiosInstance.get(`/InventoryItem/imei/${imei}`);
  return response.data;
};

/**
 * Get stock summary for a variant
 * @param {number} variantId
 */
export const getVariantStockSummary = async (variantId) => {
  const response = await axiosInstance.get(`/InventoryItem/variant/${variantId}/summary`);
  return response.data;
};

/**
 * Get sold items with optional date range
 * @param {string} startDate - ISO date string
 * @param {string} endDate - ISO date string
 */
export const getSoldItems = async (startDate, endDate) => {
  const params = {};
  if (startDate) params.startDate = startDate;
  if (endDate) params.endDate = endDate;
  const response = await axiosInstance.get('/InventoryItem/sold', { params });
  return response.data;
};

/**
 * Update inventory item status
 * @param {number} inventoryItemId
 * @param {number} newStatus - 1=Available, 2=Sold, 3=Reserved, 4=Returned, 5=Damaged, 6=InTransit
 * @param {string} notes
 */
export const updateItemStatus = async (inventoryItemId, newStatus, notes = '') => {
  const response = await axiosInstance.patch('/InventoryItem/status', {
    inventoryItemId,
    newStatus,
    notes,
  });
  return response.data;
};

/**
 * Get items by store with optional status filter
 * @param {number} storeId
 * @param {number|null} status
 */
export const getItemsByStore = async (storeId, status = null) => {
  const params = status !== null ? { status } : {};
  const response = await axiosInstance.get(`/InventoryItem/store/${storeId}`, { params });
  return response.data;
};

/**
 * Get paginated inventory items with filters
 */
export const getInventoryItemsPaginated = async ({
  pageNumber = 1,
  pageSize = 10,
  searchTerm = null,
  status = null,
  variantId = null,
  storeId = null,
  startDate = null,
  endDate = null,
} = {}) => {
  const params = { pageNumber, pageSize };
  if (searchTerm) params.searchTerm = searchTerm;
  if (status !== null) params.status = status;
  if (variantId) params.variantId = variantId;
  if (storeId) params.storeId = storeId;
  if (startDate) params.startDate = startDate;
  if (endDate) params.endDate = endDate;
  const response = await axiosInstance.get('/InventoryItem', { params });
  return response.data;
};

export const INVENTORY_ITEM_STATUS = {
  1: { label: 'Available', color: 'success' },
  2: { label: 'Sold', color: 'default' },
  3: { label: 'Reserved', color: 'warning' },
  4: { label: 'Returned', color: 'info' },
  5: { label: 'Damaged', color: 'error' },
  6: { label: 'In Transit', color: 'secondary' },
};

/**
 * Get inventory items by variant with comprehensive filtering
 * @param {number} variantId - Product variant ID
 * @param {Object} filters - Filter options
 * @returns {Promise} Response with inventory items
 */
export const getInventoryItemsByVariant = async (variantId, filters = {}) => {
  const params = {
    pageNumber: filters.pageNumber || 1,
    pageSize: filters.pageSize || 50,
  };

  if (filters.statuses && filters.statuses.length > 0) {
    params.statuses = filters.statuses.join(',');
  }
  if (filters.searchTerm && filters.searchTerm.trim()) {
    params.searchTerm = filters.searchTerm.trim();
  }
  if (filters.storeId) {
    params.storeId = filters.storeId;
  }

  const response = await axiosInstance.get(
    `/InventoryItem/variant/${variantId}`,
    { params }
  );
  return response.data;
};

/**
 * Delete a single inventory item
 * @param {number} itemId - Inventory item ID
 * @returns {Promise} Deletion confirmation
 */
export const deleteInventoryItem = async (itemId) => {
  const response = await axiosInstance.delete(`/InventoryItem/${itemId}`);
  return response.data;
};

/**
 * Bulk delete inventory items
 * @param {number[]} itemIds - Array of inventory item IDs
 * @returns {Promise} Deletion confirmation
 */
export const bulkDeleteInventoryItems = async (itemIds) => {
  const response = await axiosInstance.post('/InventoryItem/bulk-delete', { itemIds });
  return response.data;
};

/**
 * Get detailed inventory item by ID
 * @param {number} itemId - Inventory item ID
 * @returns {Promise} Inventory item details
 */
export const getInventoryItemById = async (itemId) => {
  const response = await axiosInstance.get(`/InventoryItem/${itemId}`);
  return response.data;
};

/**
 * Update inventory item
 * @param {number} itemId - Inventory item ID
 * @param {Object} updateData - Data to update
 * @returns {Promise} Updated inventory item
 */
export const updateInventoryItem = async (itemId, updateData) => {
  const response = await axiosInstance.put(`/InventoryItem/${itemId}`, updateData);
  return response.data;
};
